import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Tooltip, Spin } from 'antd';
import { useRequest } from 'ahooks';
import { apiV2HighEnergyDeviceAnalysisOverviewPost } from '@maxtropy/device-customer-apis-v2';
import { keepTwoOrLine, keepZeroOrLine } from '../../../../utils/utils';
import iconDevice from '../../imgs/icon-device.png';
import iconPower from '../../imgs/icon-power.png';
import styles from './index.module.scss';
import { isNil } from 'lodash-es';

export interface TimeParams {
  startTime: number;
  endTime: number;
  timeResolution: string;
  deviceTypeId?: number;
}

export interface StatInfoProps {
  timeParams: TimeParams;
}

const StatInfo: React.FC<StatInfoProps> = ({ timeParams }) => {
  const { startTime, endTime, timeResolution } = timeParams;

  const { data: overviewData, loading } = useRequest(
    () =>
      apiV2HighEnergyDeviceAnalysisOverviewPost({
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        timeResolution,
      }),
    {
      ready: !!startTime && !!endTime,
      refreshDeps: [startTime, endTime, timeResolution],
    }
  );

  const deviceTotal = keepZeroOrLine(overviewData?.totalCount);
  const totalRatedPower = keepTwoOrLine(overviewData?.totalRatedPower);
  const categories = overviewData?.categories ?? [];

  if (loading) {
    return (
      <div className={styles.statInfo}>
        <Spin spinning={loading} />
      </div>
    );
  }

  const tooltipContent = (
    <div className={styles.tooltipContent}>
      {categories.map(item => (
        <div className={styles.tooltipItem} key={item.deviceTypeId}>
          <span className={styles.deviceName}>
            {item.deviceTypeName}（{item.deviceCount}）
          </span>
          <Tag className={styles.powerTag}>额定：{keepTwoOrLine(item.ratedPower)}kW</Tag>
          <Tag className={styles.ratioTag}>{keepTwoOrLine(item.percentage)}%</Tag>
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.statInfo}>
      <div className={styles.statItem}>
        <img src={iconDevice} alt="设备" className={styles.statIcon} />
        <div className={styles.statContent}>
          <span className={styles.statLabel}>高能耗设备总数</span>
          <span className={styles.statValue}>{deviceTotal}</span>
        </div>
      </div>
      <div className={styles.statItem}>
        <img src={iconPower} alt="功率" className={styles.statIcon} />
        <div className={styles.statContent}>
          <span className={styles.statLabel}>高能耗设备总额定功率</span>
          <span className={styles.statValue}>{totalRatedPower}kW</span>
        </div>
      </div>
      {categories && categories.length > 0 && (
        <Tooltip
          title={tooltipContent}
          placement="left"
          align={{ offset: [-50, 0] }}
          overlayClassName={styles.statTooltip}
        >
          <InfoCircleOutlined className={styles.infoIcon} />
        </Tooltip>
      )}
    </div>
  );
};

export default StatInfo;
